import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import { MissingNumberOptions, OperationOptions, Operations, ProblemData, ProblemFormatOptions, RegroupingOptions, StoryTypeOptions } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { generateFractionPages } from "./FractionGenerator.ts";
import { generateMoneyPages } from "./MoneyGenerator.ts";
import { generateDecimalPages } from "./DecimalGenerator.ts";
import { generatePlaceValuePages } from "./PlaceValueGenerator.ts";
import { generateRoundingComparingPages } from "./RoundingComparingGenerator.ts";
import { generateTimePages } from "./TimeGenerator.ts";
import { generateOrderOfOperationsPages } from "./OrderOfOperationsGenerator.ts";
import { generateFactFamilyPages } from "./FactFamilyGenerator.ts";
import { generateAlgebraPages } from "./AlgebraGenerator.ts";
import { generateIntegerPages } from './IntegerGenerator.ts';
import { generateNumberPropertyPages } from './NumberPropertyGenerator.ts';
import { generateConversionPages } from './ConversionGenerator.ts';
import { generatePatternPages } from './PatternGenerator.ts';
import { generateRomanPages } from './RomanNumeralGenerator.ts';
import { generateNumberLinePages } from './NumberLineGenerator.ts';
import { generatePercentPages } from './PercentGenerator.ts';
import { generateRatioPages } from './RatioGenerator.ts';
import { generateGeometryPages } from './GeometryGenerator.ts';
import { generateShapePages } from './ShapeGenerator.ts';
import { generateVolumePages } from './VolumeGenerator.ts';
import { generateCirclePages } from './CircleGenerator.ts';
import { generateProbabilityPages } from './ProbabilityGenerator.ts';
import { generatePythagoreanPages } from './PythagoreanGenerator.ts';
import { generateAveragesPages } from './AveragesGenerator.ts';
import { generateBarGraphPages } from './BarGraphGenerator.ts';
import { generateLinePlotPages } from './LinePlotGenerator.ts';
import { generateHistogramPages } from './HistogramGenerator.ts';
import { generateBoxPlotPages } from './BoxPlotGenerator.ts';
import { generateCalendarPages } from './CalendarGenerator.ts';
import { generateCoordinatePages } from './CoordinatePlaneGenerator.ts';
import { generateSlopePages, isSlopeQuestion } from './SlopeGenerator.ts';
import { generateSpiralReviewPages } from "./SpiralReviewGenerator.ts";
import { generateColorByNumberPages } from "./ColorByNumberGenerator.ts";
import { generateRiddlePages } from "./RiddleGenerator.ts";
import { generateMagicSquarePages } from "./MagicSquareGenerator.ts";
import { attachVariedStories, attachWordProblems } from "./WordProblemGenerator.ts";
import { applyMissingNumbers } from "./MissingNumber.ts";
import { problemFromFactKey } from "./FactKeys.ts";
import { levelApplies } from "./FactLevels.ts";
import { dealLevelPages } from "./FactLevelGenerator.ts";

export function GenerateDataV2(settings: ISettingsProps) {
  // Place value is not arithmetic - there are no operands and no operator - so
  // it is answered before the operator selection is even looked at.
  if (settings?.Format === ProblemFormatOptions.PlaceValue) {
    return generatePlaceValuePages(settings);
  }
  if (settings?.Format === ProblemFormatOptions.RoundingComparing) {
    return generateRoundingComparingPages(settings);
  }
  if (settings?.Format === ProblemFormatOptions.TellingTime) {
    return generateTimePages(settings);
  }
  // Order of operations is arithmetic but its problems are expression trees,
  // not operand pairs, so it dispatches before the operator selection is read.
  if (settings?.Format === ProblemFormatOptions.OrderOfOperations) {
    return generateOrderOfOperationsPages(settings);
  }

  // A shape question is a drawing with a question about it - no operator to
  // select, so it answers before the operator selection is read.
  if (settings?.Format === ProblemFormatOptions.Shapes) {
    return generateShapePages(settings);
  }

  // A box is the same: a drawing with a question about it, no operator
  // (ADR 0095).
  if (settings?.Format === ProblemFormatOptions.Volume) {
    return generateVolumePages(settings);
  }

  // A circle likewise: a drawing with a question about it (ADR 0106).
  if (settings?.Format === ProblemFormatOptions.Circles) {
    return generateCirclePages(settings);
  }

  // Spinners and a bag of counters, with a question about each (ADR 0108).
  if (settings?.Format === ProblemFormatOptions.Probability) {
    return generateProbabilityPages(settings);
  }

  // A right triangle, or three lengths to test, with a question about it (ADR 0109).
  if (settings?.Format === ProblemFormatOptions.PythagoreanTheorem) {
    return generatePythagoreanPages(settings);
  }

  // Averages ask about a whole list, not two operands with an operator
  // applied to them - there is nothing for the operator bar to mean here
  // (ADR 0039 §1).
  if (settings?.Format === ProblemFormatOptions.Averages) {
    return generateAveragesPages(settings);
  }

  // A bar graph is a drawing with a scale, not two operands with an operator
  // applied to them - the question type names what to do with the chart
  // (ADR 0039 §3).
  if (settings?.Format === ProblemFormatOptions.BarGraphs) {
    return generateBarGraphPages(settings);
  }

  // A line plot is a data set drawn on a number line, asked several questions
  // per plot like a bar graph (ADR 0084 §1).
  if (settings?.Format === ProblemFormatOptions.LinePlots) {
    return generateLinePlotPages(settings);
  }

  // A histogram is a bar graph of intervals, three questions to a drawing
  // (ADR 0099 §1).
  if (settings?.Format === ProblemFormatOptions.Histograms) {
    return generateHistogramPages(settings);
  }

  // A box plot is read off a number line, three questions to a drawing
  // (ADR 0099 §1).
  if (settings?.Format === ProblemFormatOptions.BoxPlots) {
    return generateBoxPlotPages(settings);
  }

  // A month, asked several questions like a line plot (ADR 0093 §1).
  if (settings?.Format === ProblemFormatOptions.Calendar) {
    return generateCalendarPages(settings);
  }

  // A magic square is a grid with a total; no operands, no operator (ADR 0088 §4).
  if (settings?.Format === ProblemFormatOptions.MagicSquares) {
    return generateMagicSquarePages(settings);
  }

  // A grid of labelled points, read rather than plotted (ADR 0085 §1).
  if (settings?.Format === ProblemFormatOptions.CoordinatePlane) {
    // Slope draws a grid a problem, or none, not one beside a group (ADR 0126).
    if (isSlopeQuestion(settings.CoordinateQuestion)) {
      return generateSlopePages(settings);
    }
    return generateCoordinatePages(settings);
  }

  // A geometry question is a sentence about a figure described in words - an
  // angle measure, three side lengths, a polygon's name. Nothing is drawn
  // (ADR 0049 §2) and there is no operand pair for an operator to sit between.
  if (settings?.Format === ProblemFormatOptions.Geometry) {
    return generateGeometryPages(settings);
  }

  // A ratio is a relationship between two numbers, not an operand pair with an
  // operator applied to it (ADR 0035 §1).
  if (settings?.Format === ProblemFormatOptions.Ratio) {
    return generateRatioPages(settings);
  }

  // A percent question has operands but no operator to select - the question
  // type names what is being done (ADR 0034 §2).
  if (settings?.Format === ProblemFormatOptions.Percent) {
    return generatePercentPages(settings);
  }

  // A number line is a drawing with a question about it - no operands, no
  // operator, so it answers with the non-arithmetic formats.
  if (settings?.Format === ProblemFormatOptions.NumberLine) {
    return generateNumberLinePages(settings);
  }

  // A sequence is not arithmetic at all - no operands, no operator - so it
  // answers with the non-arithmetic formats above the operator selection.
  if (settings?.Format === ProblemFormatOptions.NumberPatterns) {
    return generatePatternPages(settings);
  }

  // A numeral is converted, not combined with another, so it answers with the
  // non-arithmetic formats above the operator selection (ADR 0079 §1).
  if (settings?.Format === ProblemFormatOptions.RomanNumerals) {
    return generateRomanPages(settings);
  }

  // A conversion has one value and one unit pair, and no operator to select.
  if (settings?.Format === ProblemFormatOptions.UnitConversion) {
    return generateConversionPages(settings);
  }

  // Number properties ask about one or two numbers without an operator to
  // select, so like integers they dispatch before the operator selection.
  if (settings?.Format === ProblemFormatOptions.NumberProperties) {
    return generateNumberPropertyPages(settings);
  }

  // Integers name their operations in the question type rather than taking
  // them from the operator bar, so like order of operations they dispatch
  // before the operator selection is read - and unlike algebra, which is why
  // the two are not next to each other (ADR 0030 §3).
  if (settings?.Format === ProblemFormatOptions.Integers) {
    return generateIntegerPages(settings);
  }

  // Algebra reads the operator selection, so it sits with the arithmetic
  // formats rather than the non-arithmetic block above (ADR 0028 §4).
  if (settings?.Format === ProblemFormatOptions.Algebra) {
    return generateAlgebraPages(settings);
  }
  // A fact family is a group of three related numbers, generated and laid out
  // as one unit - its own generator, dispatched before the operand path.
  if (settings?.Format === ProblemFormatOptions.FactFamily) {
    return generateFactFamilyPages(settings);
  }
  // Spiral review composes the other formats into labelled sections; it hands
  // GenerateDataV2 back to the section generator for each one.
  if (settings?.Format === ProblemFormatOptions.SpiralReview) {
    return generateSpiralReviewPages(settings, GenerateDataV2);
  }
  // Color by number places arithmetic facts into a picture; it asks this
  // function back for them as a plain fact sheet, so every rule the fact
  // sheets enforce applies (ADR 0058 §3).
  if (settings?.Format === ProblemFormatOptions.ColorByNumber) {
    return generateColorByNumberPages(settings, GenerateDataV2);
  }
  // A riddle decoder keys letters to answers the same way (ADR 0088 §3).
  if (settings?.Format === ProblemFormatOptions.RiddleDecoder) {
    return generateRiddlePages(settings, GenerateDataV2);
  }

  if (settings?.Operators === null || settings?.Operators?.length === 0) {
    return null;
  }

  // Fractions and money are built from their own ranges rather than the
  // selected digit buckets, so each has its own generator. Same page shape out.
  if (settings?.Format === ProblemFormatOptions.Fractions) {
    return generateFractionPages(settings);
  }
  if (settings?.Format === ProblemFormatOptions.Money) {
    return generateMoneyPages(settings);
  }
  if (settings?.Format === ProblemFormatOptions.Decimals) {
    return generateDecimalPages(settings);
  }

  // A fact fluency level deals its facts rather than drawing them from the
  // ranges, which it ignores (ADR 0071 §3). Missing numbers still apply: a
  // level test can leave a factor blank like any fact sheet.
  if (levelApplies(settings)) {
    return applyMissingNumbers(dealLevelPages(settings), settings);
  }

  if (settings?.NumberRanges[0].NumberRangeSelection.length === 0 || settings?.NumberRanges[1].NumberRangeSelection.length === 0) {
    return null;
  }

  // "William had 0 muffins and gave 0 away" is valid arithmetic and a poor
  // story, and about one word problem in ten was drawing a zero. Word problems
  // leave it out of the pool - unless a range holds nothing else, in which case
  // a dull problem beats no problem.
  if (settings?.Format === ProblemFormatOptions.WordProblem) {
    settings = {
      ...settings,
      NumberRanges: settings.NumberRanges.map((range) => {
        const withoutZero = range.NumberRangeSelection.filter((value) => value !== 0);
        return withoutZero.length > 0
          ? { ...range, NumberRangeSelection: withoutZero }
          : range;
      }),
    };
  }

  // A Varied word-problem sheet deals its operations evenly on every page and
  // tells its stories by type (ADR 0073 sections 2 and 3). Simple sheets do not
  // come through here, so what they draw is not touched.
  if (
    settings?.Format === ProblemFormatOptions.WordProblem &&
    settings.StoryTypes === StoryTypeOptions.Varied
  ) {
    return attachVariedStories(buildBalancedPages(settings), settings);
  }

  let problems: ProblemData[] = [];
  let pages: ProblemData[][] = [];

  const totalNumberOfProblemsNeeded = settings.NumberOfPages * settings.NumberOfProblemsPerPage;
  const operators = settings?.Operators?.map(op => op.value);
  const numberOfProblemsForEachOperator = Math.ceil(totalNumberOfProblemsNeeded / operators.length);

  let loops = 0;
  while (problems.length < totalNumberOfProblemsNeeded && loops < 10) {
    loops++;
    for (let i = 0; i < operators.length; i++) {
      const newProblems = generateRequiredProblems(settings, operators[i], numberOfProblemsForEachOperator);
      if (newProblems.length === 0) {
        continue;
      } else {
        problems = problems.concat(newProblems);
      }
    }
  }
  problems = shuffle(problems).slice(0, totalNumberOfProblemsNeeded);

  // validate problems are correct and dispersed properly. Want to avoid duplicates and inverse problems next to each other. As well as problems in sequence.
  // currently only fixing duplicates next to each other
  // todo - fix inverse problems next to each other
  problems = checkAndFixDuplicatesNextToEachOther(problems);

  for (let i = 0; i < settings.NumberOfPages; i++) {
    const start = i * settings.NumberOfProblemsPerPage;
    const end = (i + 1) * settings.NumberOfProblemsPerPage;
    let problemsPerPage = problems.slice(start, end);
    pages.push(problemsPerPage);
  }

  // A word problem is the same arithmetic wearing a story. Attaching it here
  // rather than in the renderer is what keeps the answer key describing the
  // worksheet it belongs to.
  if (settings?.Format === ProblemFormatOptions.WordProblem) {
    return attachWordProblems(pages, settings);
  }

  // A missing-number problem is the same arithmetic with a different cell left
  // blank. Stamped here, after slicing, for the same reason: the worksheet and
  // its key are rendered from this data in two passes and must agree.
  return applyMissingNumbers(pages, settings);
}

/**
 * Pages in which every selected operation appears floor(n / operations) times,
 * and the remainder goes to a random few of them (ADR 0073 section 3, which is
 * ADR 0041's rule applied to each page).
 *
 * The ordinary path cannot be leaned on for this. It draws ceil(total / ops)
 * of each operation, shuffles, and cuts to the page, which is exact only when
 * the count divides. Measured over 1000 seeds a case, the two that do not are
 * four operations at 6 a page (draw 8, cut 2 at random: 12.7% of pages lose an
 * operation) and three at 4 a page (draw 6, cut 2: 18.5%).
 */
function buildBalancedPages(settings: ISettingsProps): ProblemData[][] {
  // `settings` has already had zeros taken out of its ranges, as a word problem needs.
  const operators = settings.Operators.map((op) => op.value);
  const perPage = settings.NumberOfProblemsPerPage;
  const base = Math.floor(perPage / operators.length);
  const extras = perPage % operators.length;

  const pages: ProblemData[][] = [];
  for (let pageIndex = 0; pageIndex < settings.NumberOfPages; pageIndex++) {
    const withExtra = new Set(shuffle(operators.map((_, i) => i)).slice(0, extras));
    let page: ProblemData[] = [];
    // A narrow range or a regrouping filter can return fewer than asked, so
    // top up round by round rather than leave a page short.
    for (let attempt = 0; attempt < 10 && page.length < perPage; attempt++) {
      for (let i = 0; i < operators.length; i++) {
        const want = attempt === 0 ? base + (withExtra.has(i) ? 1 : 0) : 1;
        if (page.length >= perPage || want === 0) {
          continue;
        }
        page = page.concat(generateRequiredProblems(settings, operators[i], want).slice(0, want));
      }
    }
    page = checkAndFixDuplicatesNextToEachOther(shuffle(page).slice(0, perPage));
    pages.push(page.map((problem, index) => ({ ...problem, key: index + 1 })));
  }
  return pages;
}

function generateRequiredProblems(settings: ISettingsProps, operator: Operations, numberOfProblemsNeeded: number) {
  let problems: ProblemData[] = [];
    // get all possible combinations of numbers
    let firstDigits = settings.NumberRanges[0].NumberRangeSelection;
    let secondDigits = settings.NumberRanges[1].NumberRangeSelection;

    if (settings.NumberRanges[0].NumberOfDigits > 1) {
      firstDigits = fillUpNumberRange(settings.NumberRanges[0].NumberOfDigits, firstDigits);
    }

    if (settings.NumberRanges[1].NumberOfDigits > 1) {
      secondDigits = fillUpNumberRange(settings.NumberRanges[1].NumberOfDigits, secondDigits);
    }

    // create duplicates of the numbers to fill the total number of problems
    while (problems.length < numberOfProblemsNeeded) {
      let newProblems = generateProblems(
        firstDigits,
        secondDigits,
        numberOfProblemsNeeded,
        operator,
        settings.Regrouping,
        settings.Remainders,
        // A blank in a sentence built on a zero has no single answer.
        Boolean(settings.MissingNumber) && settings.MissingNumber !== MissingNumberOptions.Off
      );
      if (newProblems.length === 0) {
        return problems;
      }
      problems = problems.concat(newProblems);
    }

    return problems;
}

function fillUpNumberRange(numberOfDigits: number, numberRange: number[]) {
  let newNumberRange: number[] = [];
  for (let i = 0; i < numberRange.length; i++) {
    let currentNumber = numberRange[i];
    for (let j = 0; j < 10; j++) {
      if (numberOfDigits === 2) {
        newNumberRange.push(currentNumber + j);
      }
      if (numberOfDigits === 3) {
        for (let k = 0; k < 10; k++) {
          newNumberRange.push(currentNumber + (j * 10) + k);
        }
      }
    }
  }
  return newNumberRange;
}

function checkAndFixDuplicatesNextToEachOther(problems: ProblemData[]) {
  let swaps = 1;
    let loops = 0;
    while (swaps !== 0 && loops < 10) {
      swaps = 0;
      loops++;
      for (let i = 0; i < problems.length; i++) {
        if (i > 0) {
          const previousProblem = problems[i - 1];
          const currentProblem = problems[i];
          if (previousProblem.numbers[0] === currentProblem.numbers[0] && previousProblem.numbers[1] === currentProblem.numbers[1]) {
            // swap the current problem with a random problem
            let randomIndex = getRandomInt(i, problems.length - 1);
            let randomProblem = problems[randomIndex];
            while (randomProblem.numbers === currentProblem.numbers) {
              randomIndex = getRandomInt(0, problems.length - 1);
              randomProblem = problems[randomIndex];
            }
            problems[randomIndex] = currentProblem;
            problems[i] = randomProblem;
            swaps++;
          }
        }
      }
    }
    return problems;
}

/**
 * A problem as it is laid out on the page: [top, bottom, answer], with the
 * remainder appended for long division. Returns null when the drawn operands
 * cannot make a valid problem.
 */
interface BuiltProblem {
  numbers: number[];
  operator: string;
  remainder: number;
}

function signFor(operation: Operations) {
  return OperationOptions.find(op => op.value === operation)?.sign ?? '';
}

function ascending(first: number, second: number) {
  return first <= second ? [first, second] : [second, first];
}

/** Addition never borrows, so "no regrouping" just orders the operands. */
function buildAddition(first: number, second: number, regrouping: RegroupingOptions): BuiltProblem {
  const [bottom, top] =
    regrouping === RegroupingOptions.NoRegrouping ? ascending(first, second) : [first, second];

  return { numbers: [top, bottom, top + bottom], operator: signFor(Operations.Addition), remainder: 0 };
}

/** The larger operand always goes on top so the answer stays positive. */
function buildSubtraction(first: number, second: number): BuiltProblem {
  const [bottom, top] = ascending(first, second);

  return { numbers: [top, bottom, top - bottom], operator: signFor(Operations.Subtraction), remainder: 0 };
}

function buildMultiplication(first: number, second: number): BuiltProblem {
  return {
    numbers: [second, first, first * second],
    operator: signFor(Operations.Multiplication),
    remainder: 0,
  };
}

/**
 * Division is generated backwards: pick the divisor and quotient, then multiply
 * to get a dividend that divides evenly (plus a remainder, when asked for).
 */
function buildDivision(
  first: number,
  second: number,
  operation: Operations,
  includeRemainder: boolean
): BuiltProblem | null {
  let divisor = first;
  let quotient = second;
  if (divisor === 0) {
    [divisor, quotient] = [quotient, divisor];
  }

  if (divisor === 0) {
    // Both operands were zero, so there is no divisor to work with. This used
    // to be checked for long division only, and plain division printed 0 ÷ 0;
    // it was hard to reach while the default first number was 5, and easy once
    // the default became the whole digit (ADR 0075).
    return null;
  }

  const isLongDivision = operation === Operations.LongDivision;

  const remainder = isLongDivision && includeRemainder ? getRandomInt(0, divisor - 1) : 0;

  return {
    numbers: [divisor * quotient + remainder, divisor, quotient],
    operator: signFor(operation),
    remainder,
  };
}

function buildProblem(
  operator: Operations,
  first: number,
  second: number,
  regrouping: RegroupingOptions,
  includeRemainder: boolean
): BuiltProblem | null {
  switch (operator) {
    case Operations.Addition:
      return buildAddition(first, second, regrouping);
    case Operations.Subtraction:
      return buildSubtraction(first, second);
    case Operations.Multiplication:
      return buildMultiplication(first, second);
    case Operations.Division:
    case Operations.LongDivision:
      return buildDivision(first, second, operator, includeRemainder);
    default:
      return null;
  }
}

/**
 * Regrouping (carrying / borrowing) only means anything for addition and
 * subtraction; the other operators are always allowed through.
 */
function passesRegroupingFilter(
  operator: Operations,
  regrouping: RegroupingOptions,
  top: number,
  bottom: number
) {
  const applies = operator === Operations.Addition || operator === Operations.Subtraction;
  if (regrouping === RegroupingOptions.Both || !applies) {
    return true;
  }
  return RegroupingCheck(top, bottom, operator, regrouping);
}

function randomFrom(numbers: number[]) {
  return numbers[getRandomInt(0, numbers.length - 1)];
}

function generateProblems(firstDigit: number[], secondDigit: number[], numberOfRequiredProblems: number,
   operator: Operations, regrouping: RegroupingOptions, includeRemainder: boolean,
   avoidAmbiguousBlanks = false) {
  let problems: ProblemData[] = [];
  let loops = 0;
  while (problems.length < numberOfRequiredProblems && loops < numberOfRequiredProblems) {
    loops++;

    const built = buildProblem(
      operator,
      randomFrom(firstDigit),
      randomFrom(secondDigit),
      regrouping,
      includeRemainder
    );
    if (built === null) {
      continue;
    }

    const [top, bottom] = built.numbers;
    if (!passesRegroupingFilter(operator, regrouping, top, bottom)) {
      continue;
    }

    // On a missing-number sheet one operand is blanked, and a sentence built on
    // a zero has no single answer: 0 x ___ = 0 and 0 ÷ ___ = 0 are true of every
    // number. About one problem in eleven was one of those on the times-tables
    // sheets. Division by zero never reaches here (buildDivision swaps it out).
    if (avoidAmbiguousBlanks && (isZeroFactor(built) || isZeroDividend(built))) {
      continue;
    }

    problems.push({
      key: problems.length + 1,
      numbers: includeRemainder ? [...built.numbers, built.remainder] : built.numbers,
      operator: built.operator,
    });
  }

  return problems;
}

/** 0 x n and n x 0: the product is 0 whichever number is blanked. */
function isZeroFactor(built: BuiltProblem): boolean {
  return built.operator === signFor(Operations.Multiplication) && (built.numbers[0] === 0 || built.numbers[1] === 0);
}

/** 0 ÷ n: the blanked divisor could be any non-zero number. */
function isZeroDividend(built: BuiltProblem): boolean {
  return built.operator === signFor(Operations.Division) && built.numbers[0] === 0;
}

function RegroupingCheck(top: number, bottom: number, operator: Operations, regrouping: RegroupingOptions) {
  const topNumberLastDigit = top % 10;
  const bottomNumberLastDigit = bottom % 10;
  let result = false;
  if (operator === Operations.Addition) {
    const sum = topNumberLastDigit + bottomNumberLastDigit;
    if (regrouping === RegroupingOptions.NoRegrouping) {
      result = sum < 10;
    } else {
      result = sum >= 10;
    }
  }

  if (operator === Operations.Subtraction) {
    const difference = topNumberLastDigit - bottomNumberLastDigit;
    if (regrouping === RegroupingOptions.NoRegrouping) {
      // Equal units digits (15 - 5) need no borrowing either.
      result = difference >= 0;
    } else {
      result = difference < 0;
    }
  }
  return result;
}

// Half the sheet is what they missed, half is ordinary practice from the same
// settings. A page of nothing but misses is a page that says "these are your
// failures", and it teaches the pattern rather than the fact. ADR 0012 §4.
const MISSED_SHARE = 0.5;

/**
 * Pages built from known facts rather than drawn from ranges (ADR 0012 §3).
 *
 * About half the sheet is the missed facts (repeated to fill that half), half
 * is ordinary practice from `settings`, shuffled together so a student cannot
 * tell which is which. Returns the same page-shaped array `GenerateDataV2` does,
 * so nothing downstream changes — a missed-facts sheet is a Math Facts sheet.
 */
export function pagesFromFacts(facts: string[], settings: ISettingsProps): ProblemData[][] {
  const missedProblems = facts
    .map((fact, i) => problemFromFactKey(fact, i))
    .filter((problem): problem is ProblemData => problem !== null);

  if (missedProblems.length === 0) {
    return [];
  }

  const total = settings.NumberOfPages * settings.NumberOfProblemsPerPage;
  const missedTarget = Math.max(1, Math.round(total * MISSED_SHARE));

  const missedHalf: ProblemData[] = [];
  for (let i = 0; i < missedTarget; i++) {
    missedHalf.push({ ...missedProblems[i % missedProblems.length], key: i });
  }

  const drawn = (GenerateDataV2(settings) ?? []).flat().slice(0, total - missedHalf.length);

  const all = shuffle([...missedHalf, ...drawn]).map((problem, i) => ({ ...problem, key: i }));

  const pages: ProblemData[][] = [];
  for (let i = 0; i < settings.NumberOfPages; i++) {
    pages.push(all.slice(i * settings.NumberOfProblemsPerPage, (i + 1) * settings.NumberOfProblemsPerPage));
  }
  return pages;
}